import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useScanWedge } from '../scanWedge';
import { makeScanGuard } from '../scanGuard';
import { scanSound } from '../scanSound';
import { acceptCode } from './acceptCode';
import CameraScanner from './CameraScanner';
import { productsText, useMyLang } from './productsText';
import { Move, MoveLine, MoveReply, Other, Shop, moveGet, post } from './scanApi';

// Move this product — by scanning every piece (Fernando 31.10, docs/PRODUCT-SCAN.md). Step 1: the sizes with the pieces
// the WMS expects on the shelf (live stock + sold-not-picked) and a tally per size; every piece is scanned — the camera
// stays open between pieces, a Bluetooth scanner or the typed box work too; a barcode of another product is refused
// and named. Step 2: the new shelf, typed or its label scanned; what WILL be written per size is shown (counted −
// expected, plus and minus as they are; NOS never; pieces a picker is fetching are not missing). Save: the shelf for
// every ticked row (pushed to the shop) and the stock differences through the Inventory's ledger. The server is the
// truth: a reload shows the move as it is. -- Claude, 2026-10-31

const input = 'bg-white border border-[#9aa0a6] text-[#111111] rounded px-2 py-1.5 text-base';
const btn = 'px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-40';

export default function ProductMove() {
  const { mid = '' } = useParams<{ mid: string }>();
  const navigate = useNavigate();
  const lang = useMyLang();
  const t = productsText(lang);
  const [m, setM] = useState<Move | null>(null);
  const [shops, setShops] = useState<Record<string, Shop>>({});
  const [others, setOthers] = useState<Other[]>([]);
  const [err, setErr] = useState('');
  const [strip, setStrip] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const [typed, setTyped] = useState('');
  const [shelf, setShelf] = useState('');
  const [shelfStep, setShelfStep] = useState(false);
  const guard = useRef(makeScanGuard());
  const shopName = (id: string) => shops[id]?.name || id;

  const take = (j: MoveReply) => { if (j.move) setM(j.move); if (j.shops) setShops(j.shops); if (j.others) setOthers(j.others); };
  const load = useCallback(async () => {
    try { take(await moveGet(mid)); } catch (e: any) { setErr(e?.message || 'Not found'); }
  }, [mid]);
  useEffect(() => { load(); }, [load]);

  const act = async (path: string, body: any = {}): Promise<any | null> => {
    setBusy(true); setErr('');
    try {
      const r = await post(`/move/${mid}${path}`, body);
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) {
        const d = j.detail;
        if (d && typeof d === 'object' && d.code === 'foreign') {
          const p = d.product || {};
          setStrip({ tone: 'bad', text: t.moveForeign(p.name || p.article_number || '?', p.size || '', p.stock_location || t.empty) });
        } else if (d && typeof d === 'object' && d.code === 'unknown') {
          setStrip({ tone: 'bad', text: t.moveUnknown });
        } else {
          setErr(typeof d === 'string' ? d : d?.message || `HTTP ${r.status}`);
        }
        scanSound('error');
        return null;
      }
      take(j);
      return j;
    } finally { setBusy(false); }
  };

  /** every code source lands here: a piece while scanning, the shelf label on the shelf step */
  const onScan = useCallback((raw: string) => {
    const code = acceptCode(raw) || raw.trim();
    if (!code) return;
    if (guard.current(code)) return;
    if (shelfStep || m?.status === 'target') { setShelf(code); chooseShelf(code); return; }
    if (m?.status !== 'scanning') return;
    act('/scan', { code }).then(j => {
      if (j) { scanSound('ok'); setStrip({ tone: 'ok', text: t.moveScanned(j.size || '', j.counted, j.expected ?? 0) }); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.status, shelfStep, t]);
  useScanWedge(c => onScan(c), { enabled: !camera, skipFields: true });

  const chooseShelf = async (code: string) => {
    const j = await act('/target', { code });
    if (j) { scanSound('ok'); setShelfStep(false); setCamera(false); }
  };
  const save = async () => { const j = await act('/save'); if (j) { scanSound('ok'); } };
  const cancel = async () => { if (window.confirm(t.moveCancelConfirm)) { const j = await act('/cancel'); if (j) navigate('/products'); } };

  if (!m) return <div className="min-h-screen bg-[#e5e7eb] p-6 text-[#5f6368]">{err || '…'}</div>;
  const pool = m.lines.filter(l => l.pool);
  const alsoMoves = m.lines.filter(l => !l.pool);
  const step = m.status === 'scanning' ? (shelfStep ? 2 : 1) : m.status === 'target' ? 2 : 3;
  const totalC = pool.reduce((n, l) => n + (l.counted || 0), 0);
  const totalE = pool.reduce((n, l) => n + (l.infinite ? 0 : (l.expected || 0)), 0);
  const inFlight = pool.reduce((n, l) => n + (l.in_flight || 0), 0);
  const writesOff = (m.status === 'saved' ? m.writes_mode : m.writes_mode_now) !== 'on';

  return (
    <div className="min-h-screen bg-[#e5e7eb] text-[#111111]"><div className="p-3 sm:p-6 max-w-3xl">
      {camera && <CameraScanner lang={lang} continuous={m.status === 'scanning' && !shelfStep} onScan={onScan} onClose={() => setCamera(false)} />}
      <button onClick={() => navigate('/products')} className="text-[#6b7280] text-sm hover:text-[#111111]">{t.backToTable}</button>
      <h1 className="text-2xl font-bold mt-1 leading-snug">{t.moveTitle(m.name || m.style_key)}</h1>
      <div className="text-sm text-[#5f6368] mt-0.5">{m.style_key} · {shopName(m.pool_shop)} · {m.from_display || t.empty}{m.to_display ? ` → ${m.to_display}` : ''}</div>
      <div className="text-sm text-[#5f6368] mt-1">{t.moveStep(step)} · {t.moveSteps[step]}</div>
      {err && <div className="mt-3 px-3 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold">{err}</div>}

      {/* step 1 — scan every piece */}
      {m.status === 'scanning' && !shelfStep && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-3">
          <div className="text-xs text-[#6b7280] mb-2">{t.moveScanHint}</div>
          {inFlight > 0 && <div className="mb-2 text-xs bg-amber-100 border border-amber-700 rounded px-2 py-1 text-amber-900">{t.pickerOnWay(inFlight)}</div>}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button onClick={() => setCamera(true)} disabled={busy} className={`${btn} bg-[#111111] hover:bg-[#333] text-white`}>📷 {t.camera}</button>
            <form className="flex gap-2 flex-1 min-w-[12rem]" onSubmit={e => { e.preventDefault(); if (typed.trim()) { onScan(typed); setTyped(''); } }}>
              <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={t.typeCode} autoComplete="off" className={`${input} flex-1 font-mono`} />
              <button type="submit" disabled={busy || !typed.trim()} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>+1</button>
            </form>
          </div>
          {strip && <div className={`mb-3 px-3 py-2 rounded-lg text-white font-semibold ${strip.tone === 'ok' ? 'bg-green-700' : 'bg-red-700'}`}>{strip.text}</div>}
          <Lines lines={pool} t={t} shopName={shopName} mode="count" />
          {pool.length === 0 && <div className="text-sm text-[#6b7280]">{t.moveNoPieces}</div>}
          <div className="mt-2 text-sm text-[#374151]">{t.moveCountedOf(totalC, totalE)}</div>
          {alsoMoves.length > 0 && (
            <div className="mt-3 text-xs text-[#6b7280]">{t.moveAlsoMoves} {alsoMoves.map(l => `${shopName(l.webshop_id)} ${l.size || ''} (${l.from_display || t.empty})`).join(', ')}</div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button disabled={busy || !m.scans.length} onClick={() => act('/unscan').then(j => j && setStrip(null))} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>{t.moveUndo}</button>
            <button disabled={busy} onClick={() => { setShelfStep(true); setCamera(false); }} className={`${btn} bg-green-700 hover:bg-green-600 text-white`}>{t.moveDone}</button>
            <button disabled={busy} onClick={cancel} className="px-3 py-2 text-[#5f6368] text-sm underline">{t.moveCancel}</button>
          </div>
        </div>
      )}

      {/* step 2a — the new shelf */}
      {m.status === 'scanning' && shelfStep && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-3">
          <div className="text-xs text-[#6b7280] mb-2">{t.moveShelfHint}</div>
          <div className="flex flex-wrap items-center gap-2">
            <input autoFocus value={shelf} onChange={e => setShelf(e.target.value)} placeholder="K 45" onKeyDown={e => { if (e.key === 'Enter' && shelf.trim()) chooseShelf(shelf); }} className={`${input} w-32 text-lg font-mono`} />
            <button disabled={busy || !shelf.trim()} onClick={() => chooseShelf(shelf)} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>{t.moveThisShelf}</button>
            <button onClick={() => setCamera(true)} disabled={busy} className={`${btn} bg-[#111111] hover:bg-[#333] text-white`}>📷 {t.camera}</button>
            <button onClick={() => setShelfStep(false)} className="px-3 py-2 text-[#5f6368] text-sm underline">{t.moveBackToScan}</button>
          </div>
        </div>
      )}

      {/* step 2b — what will happen, then Save */}
      {m.status === 'target' && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-3">
          <div className="text-lg font-semibold mb-2">{m.from_display || '?'} → {m.to_display}</div>
          {writesOff && <div className="mb-3 text-sm bg-amber-100 border border-amber-700 rounded px-3 py-2 text-amber-900">{t.moveWritesOff}</div>}
          <Lines lines={pool} t={t} shopName={shopName} mode="preview" />
          {alsoMoves.length > 0 && <div className="mt-3 text-xs text-[#6b7280]">{t.moveAlsoMoves} {alsoMoves.map(l => `${shopName(l.webshop_id)} ${l.size || ''}`).join(', ')}</div>}
          {others.length > 0 && <Others others={others} shelf={m.to_display || ''} t={t} shopName={shopName} />}
          <div className="mt-4 flex flex-wrap gap-2">
            <button disabled={busy} onClick={save} className={`${btn} bg-green-700 hover:bg-green-600 text-white`}>{busy ? t.moveSaving : t.moveSave}</button>
            <button disabled={busy} onClick={() => act('/rescan').then(j => j && setShelfStep(false))} className="px-3 py-2 text-[#5f6368] text-sm underline">{t.moveBackToScan}</button>
            <button disabled={busy} onClick={cancel} className="px-3 py-2 text-[#5f6368] text-sm underline">{t.moveCancel}</button>
          </div>
        </div>
      )}

      {/* step 3 — done */}
      {m.status === 'saved' && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-3">
          <div className="text-lg font-semibold">{t.moveSavedTitle}: {m.from_display || '?'} → {m.to_display}</div>
          <div className="text-xs text-[#6b7280] mb-2">{m.started_by} · {m.note}</div>
          {m.count_differs && (
            <div className="mb-2 text-sm text-amber-900">
              {m.missing > 0 && <span>{t.moveMissing(m.missing)} </span>}{m.surplus > 0 && <span>{t.moveSurplus(m.surplus)}</span>}
            </div>
          )}
          {writesOff && <div className="mb-3 text-sm bg-amber-100 border border-amber-700 rounded px-3 py-2 text-amber-900">{t.moveWritesOff}</div>}
          <Lines lines={pool} t={t} shopName={shopName} mode="result" />
          {alsoMoves.length > 0 && <div className="mt-3 text-xs text-[#6b7280]">{t.moveAlsoMoves} {alsoMoves.map(l => `${shopName(l.webshop_id)} ${l.size || ''}`).join(', ')}</div>}
          {others.length > 0 && <Others others={others} shelf={m.to_display || ''} t={t} shopName={shopName} />}
          <div className="mt-4 flex gap-2">
            <button onClick={() => navigate('/products')} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`}>{t.moveScanAnother}</button>
            <button onClick={load} className={`${btn} bg-[#dfe3e8] hover:bg-[#c6cdd5]`}>⟳</button>
          </div>
        </div>
      )}
      {m.status === 'cancelled' && <div className="mt-4 text-sm text-[#5f6368]">{t.moveCancelled}</div>}
    </div></div>
  );
}

function Lines({ lines, t, shopName, mode }: { lines: MoveLine[]; t: ReturnType<typeof productsText>; shopName: (id: string) => string; mode: 'count' | 'preview' | 'result' }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {lines.map(l => {
          const c = l.counted || 0; const e = l.infinite ? null : (l.expected || 0);
          const tone = l.infinite ? 'text-[#6b7280]' : e === null ? '' : c === e ? 'text-green-700' : c < e ? 'text-amber-700' : 'text-red-700';
          const d = mode === 'result' ? l.delta : l.delta_preview;
          return (
            <tr key={l.wms_id} className="border-t border-[#d1d5db]">
              <td className="py-2 font-semibold w-20">{l.size || t.empty}</td>
              <td className="text-xs text-[#6b7280]">{l.article_number}{l.from_display ? ` · ${l.from_display}` : ''}</td>
              <td className="text-right whitespace-nowrap">
                {l.infinite ? <span className="text-xs text-[#6b7280]">{t.nos}</span> : (
                  <>
                    <span className={`text-xl font-bold ${tone}`}>{c}</span>
                    <span className="text-[#6b7280]"> / {e}</span>
                    {(l.unpicked || 0) > 0 && mode === 'count' && <div className="text-[11px] text-[#6b7280]">= {l.live} + {l.unpicked} {t.soldNotPicked(l.unpicked || 0).split(' — ')[0]}</div>}
                    {(l.in_flight || 0) > 0 && <div className="text-[11px] text-amber-800">{t.moveInFlight(l.in_flight || 0)}</div>}
                  </>
                )}
              </td>
              {mode !== 'count' && (
                <td className="text-right text-xs w-40">
                  {l.infinite ? <span className="text-[#6b7280]">{t.moveNoWriteNos}</span>
                    : d === null || d === undefined || d === 0 ? <span className="text-[#6b7280]">{t.moveNoChange}</span>
                    : <span className={d < 0 ? 'text-red-700' : 'text-blue-700'}>{t.moveWillWrite(d)}</span>}
                  {mode === 'result' && l.write_status && <div className={l.write_status === 'ok' ? 'text-green-700' : l.write_status === 'review' ? 'text-red-700' : 'text-[#6b7280]'} title={l.write_reason || ''}>{t.moveWrite[l.write_status] || l.write_status}</div>}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Others({ others, shelf, t, shopName }: { others: Other[]; shelf: string; t: ReturnType<typeof productsText>; shopName: (id: string) => string }) {
  return (
    <div className="mt-3">
      <div className="text-xs uppercase tracking-wider text-[#6b7280]">{t.moveAlsoAt(shelf)}</div>
      {others.map(g => (
        <div key={g.key} className="text-sm mt-1">
          <span className="font-medium">{g.name || g.master_sku}</span> <span className="text-xs text-[#6b7280]">{shopName(g.webshop_id)} · {g.sizes.map(s => `${s.size} (${s.stock})`).join(', ')}</span>
        </div>
      ))}
    </div>
  );
}
